/**
 * Slug derivation for the writing-first editor.
 *
 * The Gridea-style editor lets the author start typing immediately; the slug is
 * derived from the title behind their back and stays editable in the settings
 * drawer. The server re-validates whatever ends up here (AGENTS.md §10) — this
 * function only has to produce a reasonable default.
 */

/**
 * Derive a URL-safe slug from a title.
 *
 * Keeps letters, digits and single dashes. Titles in scripts without a
 * latinization (Chinese above all) reduce to nothing here, and `fallback` —
 * usually a timestamp slug — takes over rather than emitting an empty slug.
 */
export function deriveSlugFromTitle(title: string, fallback: string): string {
  const slug = title
    .toLowerCase()
    .normalize("NFKD")
    // Accented latin folds onto its base (é -> e). CJK has no decomposition,
    // which is exactly the case the fallback covers.
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");

  return slug === "" ? fallback : slug;
}

/** A timestamp slug: deterministic, unique enough for a draft, never empty. */
export function fallbackSlug(now: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");

  return `post-${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}-${pad(
    now.getUTCHours(),
  )}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`;
}
